// The Catalog page (#2007): every article of the business under its product group and — for the
// platform owner — the import. Business admins maintain their own articles and values; importing
// a BankettProfi file retains owner authority. The editor of service descriptions was removed
// from this page (#2186); saved descriptions stay on the server.
import React from "react";
import { Banner, Button } from "@/ds";
import { Icon } from "@/lib";
import { cockpit } from "@/api";
import type { WorkflowBusiness } from "@/api";
import type { PushToast } from "@/notifications/toast-queue";
import { useT } from "../i18n";
import { useLiveInvalidation } from "../live/live-provider";
import { CatalogArticleDialog, CatalogArticles } from "./catalog-articles";
import type { CatalogItem } from "./catalog-articles.logic";
import { TenantCatalogEditor } from "./screen-tenant-catalog";
import styles from "./screen-catalog.module.css";

export interface CatalogScreenProps {
  tenantId: string;
  canImport?: boolean;
  pushToast: PushToast;
  onBackToRequest?: () => void;
}

/**
 * The dialog that is open: a new article (`null`), or the one being changed as it was when the
 * dialog opened — a save against a state that has moved on since is the server's to refuse.
 */
type ArticleEditor = { item: CatalogItem | null };

export function CatalogScreen({ tenantId, pushToast, canImport = false, onBackToRequest }: CatalogScreenProps) {
  const t = useT();
  const [data, setData] = React.useState<WorkflowBusiness | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [editor, setEditor] = React.useState<ArticleEditor | null>(null);
  const readSeq = React.useRef(0);

  const load = React.useCallback(() => {
    const seq = ++readSeq.current;
    cockpit.getWorkflowBusiness().then(
      (next) => { if (seq === readSeq.current) { setData(next); setFailed(false); } },
      () => { if (seq === readSeq.current) setFailed(true); },
    );
  }, []);

  React.useEffect(() => {
    // Per business: another business's articles must not stay on screen while this one loads.
    setData(null);
    setFailed(false);
    setEditor(null);
    load();
  }, [load, tenantId]);

  // An import, or an article changed in another session, changes what is listed here (#544).
  const invalidations = useLiveInvalidation("catalog");
  React.useEffect(() => {
    if (invalidations > 0) load();
  }, [invalidations, load]);

  const accept = (next: WorkflowBusiness) => {
    // A read that started before this save must not put the older state back.
    readSeq.current += 1;
    setData(next);
    setFailed(false);
  };

  const goToImport = () => {
    const heading = document.getElementById("catalog-import-title");
    const file = document.getElementById("catalog-file") as HTMLInputElement | null;
    heading?.scrollIntoView({ block: "start", behavior: "instant" });
    // Navigation never opens the chooser; the heading takes focus while an import is running.
    (file && !file.disabled ? file : heading)?.focus({ preventScroll: true });
  };

  // Opened from a request whose sources need work: the articles are what can be corrected here.
  // They start the page, so focus moves to them without scrolling the page head away.
  const focusedArticles = React.useRef(false);
  React.useEffect(() => {
    if (!onBackToRequest || !data || focusedArticles.current) return;
    const heading = document.getElementById("catalog-articles-title");
    if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); focusedArticles.current = true; }
  }, [data, onBackToRequest]);
  const items = data?.snapshot.catalog ?? null;

  return (
    <div className="page page--management page--catalog">
      {onBackToRequest && <Button variant="ghost" onClick={onBackToRequest}>{t("solGuide.backToRequest")}</Button>}
      <div className="page-head">
        <div>
          <h1>{t("catalog.title")}</h1>
        </div>
        <div className={`management-actions ${styles.headActions}`}>
          {canImport && <Button variant="secondary" onClick={goToImport}>{t("businesses.catalog.goToImport")}</Button>}
          <Button variant="primary" iconLeft={<Icon name="plus" size={16} />} disabled={!data}
            data-testid="catalog-add-article" onClick={() => setEditor({ item: null })}>
            {t("catalog.add")}
          </Button>
        </div>
      </div>
      <div className={`catalog-grid ${styles.workspace}`}>
        {failed && <Banner tone="danger" title={t("common.error")}>{t("workflow.error.source_unavailable")}</Banner>}
        <CatalogArticles items={items} failed={failed} canImport={canImport}
          onEdit={(item) => setEditor({ item })} />
        {canImport && <TenantCatalogEditor tenantId={tenantId} pushToast={pushToast} headingLevel={2} presentation="page" onImported={load} />}
      </div>
      {editor && items && (
        <CatalogArticleDialog
          key={editor.item?.item_id ?? "new"}
          item={editor.item}
          items={items}
          onClose={() => setEditor(null)}
          onSaved={accept}
          onStale={load}
          pushToast={pushToast}
        />
      )}
    </div>
  );
}
